import React from 'react';

import { Eye } from 'lucide-react';

import PlayedPill, { playedPillClasses } from './PlayedPill';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';
import ClosedBadge from '../common/ClosedBadge';

interface CourseCompactCardProps {
  course: CourseMinifiedDTO;
  // Selecting the course: centres the map on it. Supplied by the drawer's next-nearby row, where
  // tapping the row is the only thing it does.
  onSelect?: () => void;
  // Supplied by the phone strip. Opens the detail drawer on this course. When both are given this
  // one wins for a tap on the card, because it is the larger of the two actions and includes the
  // other.
  onView?: () => void;
  // The member's played state, shown and set on the card itself. Optional: the drawer's
  // next-nearby row leaves both out and renders no toggle, because the course being read already
  // has the full CourseStateToggles above it.
  isPlayed?: boolean;
  // Fires on the member's tap. Explore puts this behind the sign-in gate, so a signed-out visitor
  // gets the member-setup drawer here rather than a tick.
  onTogglePlayed?: () => void;
  isSelected?: boolean;
  className?: string;
}

// A course in four things: logo, name, country, address (D5 in docs/EXPLORE.md). No hero
// photo, no distance pill.
//
// Shared by the detail drawer's "next nearby" row and the phone strip, which are the two places a
// course is offered as a *next step* rather than as the thing being read. The full CourseCard is
// the other shape — hero, phone, share and both state checkboxes — and it is far too tall for a
// strip docked to the bottom of a phone: at full height it took 81% of the screen and left barely
// any map, which is the whole reason this exists.
//
// The whole card is the target, not a button parked in the corner of it. A card that names one
// club and shows its logo reads as a single thing to press, so that is what people press. View
// stays as a pill anyway, matching the Played one beside it: the card being pressable is not
// something a first-time visitor can see, and the pair reads as "the two things you can do here".
// It is the same action as pressing the card, said out loud.
//
// Both pills must read as pressable at a glance, so neither is allowed to look faded: a pale
// control on a dark panel reads as disabled, and neither of these ever is. View carries the club's
// gold because it is a live action. Played is a switch, and PlayedPill holds the reasoning for how
// it shows its two states.
//
// An <article role="button"> rather than a real <button>, because the pills are buttons of their
// own and a button may not contain another button. The keyboard handler below is what a real
// button would have given for free.
const CourseCompactCard: React.FC<CourseCompactCardProps> = ({
  course,
  onSelect,
  onView,
  isPlayed = false,
  onTogglePlayed,
  isSelected = false,
  className = '',
}) => {
  const activate = onView ?? onSelect;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!activate || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    // Space scrolls the page by default, and on the strip that is the map behind the card.
    event.preventDefault();
    activate();
  };

  return (
    <article
      {...(activate
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: activate,
            onKeyDown: handleKeyDown,
          }
        : {})}
      className={`flex items-center gap-3 rounded-xl border bg-white/[0.03] p-3 text-left transition-colors ${
        activate ? 'cursor-pointer' : ''
      } ${
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-white/10 hover:border-primary/60'
      } ${className}`}
    >
      {/* Same white plate as the card hero's badge, scaled down. Club logos and the GolfPlayed
        fallback are both dark ink on white, so one plate suits both. */}
      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white p-1 ring-1 ring-black/20">
        <img
          src={courseLogo(course).src}
          // Empty alt when the plate is showing a stand-in (the GolfPlayed mark, or Closed) rather
          // than this club's own logo: naming the club would describe an image that is not it.
          alt={courseLogo(course).hasOwnLogo ? course.name : ''}
          className="h-full w-full object-contain"
          draggable={false}
        />
      </span>

      <span className="min-w-0 flex-1">
        {/* No `block` next to line-clamp: the clamp sets display:-webkit-box and a display
          utility beside it wins, which silently turns the clamp off. */}
        <span className="line-clamp-2 text-sm font-bold leading-snug text-white">
          {course.name}
        </span>
        {course.countryName && (
          <span className="mt-0.5 block truncate text-xs text-primary/70">
            {course.countryName}
          </span>
        )}
        {course.closed && (
          <span className="mt-1 flex">
            <ClosedBadge
              closed={course.closed}
              closedYear={course.closedYear}
              closedMonth={course.closedMonth}
            />
          </span>
        )}
        {course.address && (
          <span className="mt-1 line-clamp-2 text-xs leading-snug text-white/35">
            {course.address}
          </span>
        )}
      </span>

      <span className="flex shrink-0 flex-col items-stretch gap-1.5 self-center">
        {onTogglePlayed && (
          <PlayedPill
            courseName={course.name ?? ''}
            isPlayed={isPlayed}
            onToggle={onTogglePlayed}
          />
        )}

        {/* Says out loud what pressing the card already does. Its own handler rather than letting
          the click bubble to the card, so it still works if the card ever stops being a target. */}
        {onView && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
            aria-label={`View ${course.name}`}
            className={`${playedPillClasses} border-primary/40 bg-primary/10 text-primary hover:border-primary hover:bg-primary/20`}
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </button>
        )}
      </span>
    </article>
  );
};

export default CourseCompactCard;
